export const getPhoneDigits = value => String(value || '').replace(/\D/g, '');

export const getPhoneNationalDigits = value => {
    const digits = getPhoneDigits(value);

    if (digits.length === 10) {
        return digits;
    }

    if (digits.length === 11 && (digits[0] === '7' || digits[0] === '8')) {
        return digits.substr(1);
    }

    return '';
};

export const isCompletePhone = value => Boolean(getPhoneNationalDigits(value));

export const formatPhone = value => {
    const digits = getPhoneNationalDigits(value);

    if (!digits) {
        return value;
    }

    return `+7 (${digits.substr(0, 3)}) ${digits.substr(3, 3)}-${digits.substr(6, 2)}-${digits.substr(8, 2)}`;
};

export const normalizePhoneForMask = value => {
    const nationalDigits = getPhoneNationalDigits(value);

    return nationalDigits
        ? `7${nationalDigits}`
        : getPhoneDigits(value);
};

const dispatchInputEvent = (input, type) => {
    const event = input.ownerDocument.createEvent('Event');
    event.initEvent(type, true, false);
    input.dispatchEvent(event);
};

const isMobileDevice = () => {
    if (typeof navigator === 'undefined') {
        return false;
    }

    return /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent)
        || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
};

const enablePhoneInputAfterClear = input => {
    if (!input || input.__phoneInputAfterClearEnabled || !isMobileDevice()) {
        return;
    }

    input.__phoneInputAfterClearEnabled = true;

    input.addEventListener('beforeinput', event => {
        if (event.inputType && event.inputType.indexOf('delete') === 0) {
            input.__phoneAmbiguousNationalDigits = undefined;
            return;
        }

        if (
            input.__phoneAmbiguousNationalDigits
            && event.data
            && /^\d$/.test(event.data)
        ) {
            const digits = `${input.__phoneAmbiguousNationalDigits}${event.data}`;

            if (digits.length === 11) {
                event.preventDefault();
                input.__phoneAmbiguousNationalDigits = undefined;

                setTimeout(() => {
                    if (!input.inputmask) {
                        return;
                    }

                    input.inputmask.setValue(formatPhone(`7${digits.substr(1)}`));
                    input.setSelectionRange(input.value.length, input.value.length);
                }, 0);

                return;
            }

            input.__phoneAmbiguousNationalDigits = digits;
        }

        if (
            input.__phoneNeedsRestartAfterClear
            && event.data
            && /^\d$/.test(event.data)
        ) {
            input.__phoneRestartDigit = event.data;
        }
    }, true);

    input.addEventListener('input', event => {
        const digitsLength = getPhoneDigits(input.value).length;

        if (input.__phoneHadCompleteValue && digitsLength <= 1) {
            input.__phoneNeedsRestartAfterClear = true;
            input.__phoneModelSyncedValue = undefined;
        }

        const digit = input.__phoneRestartDigit
            || (event.data && /^\d$/.test(event.data) ? event.data : '');

        if (!input.__phoneNeedsRestartAfterClear || !digit) {
            return;
        }

        input.__phoneRestartDigit = undefined;

        setTimeout(() => {
            if (!input.__phoneNeedsRestartAfterClear || !input.inputmask) {
                return;
            }

            input.__phoneNeedsRestartAfterClear = false;

            const value = digit === '7'
                ? '+7 ('
                : `+7 (${digit}`;
            const caretPosition = digit === '7' ? 4 : 5;

            if (digit === '8') {
                input.__phoneAmbiguousNationalDigits = digit;
            }

            input.inputmask.setValue(value);

            setTimeout(() => {
                input.setSelectionRange(caretPosition, caretPosition);
            }, 0);
        }, 0);
    }, true);
};

export const syncPhoneModel = function () {
    const input = this;

    if (!input || input.__phoneModelSyncPending || input.__phoneModelSyncedValue === input.value) {
        return;
    }

    input.__phoneModelSyncPending = true;

    setTimeout(() => {
        try {
            input.__phoneModelSyncedValue = input.value;
            dispatchInputEvent(input, 'input');
            dispatchInputEvent(input, 'change');
        } finally {
            input.__phoneModelSyncPending = false;
        }
    }, 0);
};

export const syncPhoneModelOnWrite = (event, buffer) => {
    if (!event || !event.target) {
        return;
    }

    const input = event.target;
    const digitsLength = buffer ? getPhoneDigits(buffer.join('')).length : 0;

    enablePhoneInputAfterClear(input);

    if (digitsLength < 11) {
        input.__phoneModelSyncedValue = undefined;
        return;
    }

    if (digitsLength === 11) {
        input.__phoneHadCompleteValue = true;
        input.__phoneNeedsRestartAfterClear = false;
        syncPhoneModel.call(input);
    }
};
